import {
  Box,
  Button,
  Card,
  CardContent,
  Paper,
  TextField,
  Typography,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Avatar,
  CircularProgress,
} from '@mui/material';
import { PersonAdd, MoreVert, Redo, Edit, Group } from '@mui/icons-material';
import { IoTrashOutline as DeleteIcon } from 'react-icons/io5';
import { getProfile, getToken, getCurrentOrgId } from 'client/modules/Auth/AuthReducer';
import callApi from 'client/util/apiCaller';
import React, { useState, useEffect } from 'react';
import Helmet from 'react-helmet';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

const messages = defineMessages({
  title: {
    id: 'Team.Title',
    defaultMessage: 'Team Management',
  },
  description: {
    id: 'Team.Description',
    defaultMessage: 'Manage your organization members, roles, and pending invitations.',
  },
  teamMembers: {
    id: 'Team.TeamMembers',
    defaultMessage: 'Team Members',
  },
  pendingInvitations: {
    id: 'Team.PendingInvitations',
    defaultMessage: 'Pending Invitations',
  },
  inviteTeammate: {
    id: 'Team.InviteTeammate',
    defaultMessage: 'Invite teammate',
  },
  name: {
    id: 'Team.Name',
    defaultMessage: 'Name',
  },
  email: {
    id: 'Team.Email',
    defaultMessage: 'Email',
  },
  role: {
    id: 'Team.Role',
    defaultMessage: 'Role',
  },
  actions: {
    id: 'Team.Actions',
    defaultMessage: 'Actions',
  },
  owner: {
    id: 'Team.Role.Owner',
    defaultMessage: 'Owner',
  },
  admin: {
    id: 'Team.Role.Admin',
    defaultMessage: 'Admin',
  },
  member: {
    id: 'Team.Role.Member',
    defaultMessage: 'Member',
  },
  viewer: {
    id: 'Team.Role.Viewer',
    defaultMessage: 'Viewer',
  },
  ownerDescription: {
    id: 'Team.Role.Owner.Description',
    defaultMessage: 'Full access to organization settings and billing',
  },
  adminDescription: {
    id: 'Team.Role.Admin.Description',
    defaultMessage: 'Manage team members and organization settings',
  },
  memberDescription: {
    id: 'Team.Role.Member.Description',
    defaultMessage: 'Create and manage surveys and responses',
  },
  viewerDescription: {
    id: 'Team.Role.Viewer.Description',
    defaultMessage: 'View surveys and responses only',
  },
  inviteDialog: {
    id: 'Team.InviteDialog.Title',
    defaultMessage: 'Invite new teammate',
  },
  emailAddress: {
    id: 'Team.InviteDialog.EmailAddress',
    defaultMessage: 'Email address',
  },
  selectRole: {
    id: 'Team.InviteDialog.SelectRole',
    defaultMessage: 'Select role',
  },
  sendInvitation: {
    id: 'Team.InviteDialog.Send',
    defaultMessage: 'Send invitation',
  },
  cancel: {
    id: 'Team.Cancel',
    defaultMessage: 'Cancel',
  },
  resendInvitation: {
    id: 'Team.ResendInvitation',
    defaultMessage: 'Resend invitation',
  },
  deleteInvitation: {
    id: 'Team.DeleteInvitation',
    defaultMessage: 'Delete invitation',
  },
  removeMember: {
    id: 'Team.RemoveMember',
    defaultMessage: 'Remove member',
  },
  changeRole: {
    id: 'Team.ChangeRole',
    defaultMessage: 'Change role',
  },
  invitationSent: {
    id: 'Team.InvitationSent',
    defaultMessage: 'Invitation sent successfully',
  },
  invitationResent: {
    id: 'Team.InvitationResent',
    defaultMessage: 'Invitation resent successfully',
  },
  invitationDeleted: {
    id: 'Team.InvitationDeleted',
    defaultMessage: 'Invitation deleted successfully',
  },
  memberRemoved: {
    id: 'Team.MemberRemoved',
    defaultMessage: 'Member removed successfully',
  },
  roleUpdated: {
    id: 'Team.RoleUpdated',
    defaultMessage: 'Role updated successfully',
  },
  noPermissionToChangeRole: {
    id: 'Team.NoPermissionToChangeRole',
    defaultMessage: 'You do not have permission to change this member\'s role',
  },
  cannotChangeOwnRole: {
    id: 'Team.CannotChangeOwnRole',
    defaultMessage: 'You cannot change your own role',
  },
  noPermissionToChangeRoleTooltip: {
    id: 'Team.NoPermissionToChangeRoleTooltip',
    defaultMessage: 'You don\'t have permission to change this role',
  },
  errorLoadingTeam: {
    id: 'Team.ErrorLoadingTeam',
    defaultMessage: 'Error loading team data',
  },
  errorInviting: {
    id: 'Team.ErrorInviting',
    defaultMessage: 'Error sending invitation',
  },
  sentAt: {
    id: 'Team.SentAt',
    defaultMessage: 'Sent',
  },
});

export default function TeamPage() {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const profile = useSelector(getProfile);
  const token = useSelector(getToken);
  const currentOrgId = useSelector(getCurrentOrgId);

  const [members, setMembers] = useState([]);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const roles = [
    {
      value: 'owner',
      label: formatMessage(messages.owner),
      description: formatMessage(messages.ownerDescription),
    },
    {
      value: 'admin',
      label: formatMessage(messages.admin),
      description: formatMessage(messages.adminDescription),
    },
    {
      value: 'member',
      label: formatMessage(messages.member),
      description: formatMessage(messages.memberDescription),
    },
    {
      value: 'viewer',
      label: formatMessage(messages.viewer),
      description: formatMessage(messages.viewerDescription),
    },
  ];

  useEffect(() => {
    if (currentOrgId && token) {
      loadTeamData();
    }
  }, [currentOrgId, token]);

  const loadTeamData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [membersResponse, invitationsResponse] = await Promise.all([
        callApi(dispatch, `organizations/${currentOrgId}/members`, token, 'GET'),
        callApi(dispatch, `organizations/${currentOrgId}/invitations`, token, 'GET'),
      ]);
      setMembers(membersResponse.members || []);
      setPendingInvitations(invitationsResponse.invitations || []);
    } catch (err) {
      setError(formatMessage(messages.errorLoadingTeam));
      console.error('Error loading team data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInviteTeammate = async () => {
    if (!inviteEmail || !inviteRole) {
      return;
    }

    try {
      setInviteLoading(true);
      setError(null);
      await callApi(dispatch, `organizations/${currentOrgId}/invite`, token, 'POST', {
        email: inviteEmail,
        role: inviteRole,
      });
      setSuccess(formatMessage(messages.invitationSent));
      setInviteDialogOpen(false);
      setInviteEmail('');
      setInviteRole('member');
      loadTeamData(); // Reload to show new pending invitation
    } catch (err) {
      setError(err.message || formatMessage(messages.errorInviting));
    } finally {
      setInviteLoading(false);
    }
  };

  const handleResendInvitation = async invitationId => {
    try {
      await callApi(
        dispatch,
        `organizations/${currentOrgId}/invitations/${invitationId}/resend`,
        token,
        'POST',
      );
      setSuccess(formatMessage(messages.invitationResent));
    } catch (err) {
      setError(err.message || 'Failed to resend invitation');
    }
  };

  const handleDeleteInvitation = async invitationId => {
    try {
      await callApi(
        dispatch,
        `organizations/${currentOrgId}/invitations/${invitationId}`,
        token,
        'DELETE',
      );
      setSuccess(formatMessage(messages.invitationDeleted));
      loadTeamData();
    } catch (err) {
      setError(err.message || 'Failed to delete invitation');
    }
  };

  const handleRemoveMember = async memberId => {
    try {
      await callApi(dispatch, `organizations/${currentOrgId}/members/${memberId}`, token, 'DELETE');
      setSuccess(formatMessage(messages.memberRemoved));
      loadTeamData();
    } catch (err) {
      setError(err.message || 'Failed to remove member');
    }
  };

  const handleChangeRole = async (memberId, newRole) => {
    // Find the member to check permissions
    const targetMember = members.find(m => m._id === memberId);
    if (!targetMember || !canChangeRole(targetMember)) {
      setError(formatMessage(messages.noPermissionToChangeRole));
      return;
    }

    try {
      await callApi(
        dispatch,
        `organizations/${currentOrgId}/members/${memberId}/role`,
        token,
        'PUT',
        {
          role: newRole,
        },
      );
      setSuccess(formatMessage(messages.roleUpdated));
      loadTeamData();
    } catch (err) {
      setError(err.message || 'Failed to update role');
    }
  };

  const formatDate = dateString => {
    return new Date(dateString).toLocaleDateString();
  };

  const currentMember = members.find(m => m.user?.email === profile?.email);
  const isOwnerOrAdmin = currentMember?.role === 'owner' || currentMember?.role === 'admin';
  
  // Check if current user can change roles for a specific member
  const canChangeRole = (targetMember) => {
    // Only owners and admins can change roles
    if (!isOwnerOrAdmin) return false;
    
    // Can't change your own role
    if (targetMember.user?.email === profile?.email) return false;
    
    // Can't change owner's role
    if (targetMember.role === 'owner') return false;
    
    return true;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}



      {/* Team Members */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              mb: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Group sx={{ color: 'text.secondary' }} />
              <Typography variant="h6" component="h2">
                {formatMessage(messages.teamMembers)}
              </Typography>
            </Box>
            {isOwnerOrAdmin && (
              <Button
                variant="contained"
                size="small"
                startIcon={<PersonAdd />}
                onClick={() => setInviteDialogOpen(true)}
              >
                {formatMessage(messages.inviteTeammate)}
              </Button>
            )}
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{formatMessage(messages.name)}</TableCell>
                  <TableCell>{formatMessage(messages.email)}</TableCell>
                  <TableCell>{formatMessage(messages.role)}</TableCell>
                  {isOwnerOrAdmin && <TableCell align="right">{formatMessage(messages.actions)}</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {members.map(member => (
                  <TableRow key={member._id}>
                    <TableCell sx={{ verticalAlign: 'middle' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ mr: 2, width: 32, height: 32 }} src={member.user?.picture} alt={member.user?.name || member.user?.email || '?'}>
                          {member.user?.name?.[0] || member.user?.email?.[0] || '?'}
                        </Avatar>
                        {member.user?.name || 'Unknown User'}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'middle' }}>
                      {member.user?.email || 'No email'}
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'middle' }}>
                      {member.role === 'owner' ? (
                        <Chip
                          label={roles.find(r => r.value === member.role)?.label || member.role}
                          color="primary"
                          size="small"
                        />
                      ) : canChangeRole(member) ? (
                        <FormControl
                          size="small"
                          sx={{
                            minWidth: 140,
                            marginTop: 0,
                            marginBottom: 0,
                          }}
                        >
                          <Select
                            value={member.role}
                            onChange={e => handleChangeRole(member._id, e.target.value)}
                            displayEmpty
                            sx={{
                              height: 32,
                              '& .MuiSelect-select': {
                                paddingTop: '4px',
                                paddingBottom: '4px',
                                display: 'flex',
                                alignItems: 'center',
                              },
                            }}
                          >
                            {roles
                              .filter(r => r.value !== 'owner')
                              .map(role => (
                                <MenuItem key={role.value} value={role.value}>
                                  {role.label}
                                </MenuItem>
                              ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <Tooltip title={
                          member.user?.email === profile?.email 
                            ? formatMessage(messages.cannotChangeOwnRole)
                            : formatMessage(messages.noPermissionToChangeRoleTooltip)
                        }>
                          <Chip
                            label={roles.find(r => r.value === member.role)?.label || member.role}
                            color="default"
                            size="small"
                            variant="outlined"
                          />
                        </Tooltip>
                      )}
                    </TableCell>
                    {isOwnerOrAdmin && (
                      <TableCell align="right">
                        {member.role !== 'owner' && member.user?.email !== profile?.email && (
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'flex-end' }}>
                            <Tooltip title={formatMessage(messages.removeMember)}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveMember(member._id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Pending Invitations */}
      {pendingInvitations.length > 0 && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <PersonAdd sx={{ color: 'text.secondary' }} />
              <Typography variant="h6" component="h2">
                {formatMessage(messages.pendingInvitations)}
              </Typography>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{formatMessage(messages.email)}</TableCell>
                    <TableCell>{formatMessage(messages.role)}</TableCell>
                    <TableCell>{formatMessage(messages.sentAt)}</TableCell>
                    {isOwnerOrAdmin && <TableCell align="right">{formatMessage(messages.actions)}</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingInvitations.map(invitation => (
                    <TableRow key={invitation._id}>
                      <TableCell>{invitation.email}</TableCell>
                      <TableCell>
                        <Chip
                          label={
                            roles.find(r => r.value === invitation.role)?.label || invitation.role
                          }
                          color={invitation.role === 'owner' ? 'primary' : 'default'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{formatDate(invitation.createdAt)}</TableCell>
                      {isOwnerOrAdmin && (
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                            <Tooltip title={formatMessage(messages.resendInvitation)}>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleResendInvitation(invitation._id)}
                              >
                                <Redo />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={formatMessage(messages.deleteInvitation)}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteInvitation(invitation._id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Invite Dialog */}
      <Dialog
        open={inviteDialogOpen}
        onClose={() => setInviteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{formatMessage(messages.inviteDialog)}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
            <TextField
              label={formatMessage(messages.emailAddress)}
              type="email"
              value={inviteEmail}
              onChange={e => setInviteEmail(e.target.value)}
              fullWidth
              variant="outlined"
            />
            <FormControl fullWidth>
              <InputLabel>{formatMessage(messages.selectRole)}</InputLabel>
              <Select
                value={inviteRole}
                label={formatMessage(messages.selectRole)}
                onChange={e => setInviteRole(e.target.value)}
              >
                {roles
                  .filter(r => r.value !== 'owner')
                  .map(role => (
                    <MenuItem key={role.value} value={role.value}>
                      <Box>
                        <Typography variant="body1">{role.label}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {role.description}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteDialogOpen(false)}>
            {formatMessage(messages.cancel)}
          </Button>
          <Button
            onClick={handleInviteTeammate}
            variant="contained"
            disabled={!inviteEmail || !inviteRole || inviteLoading}
            startIcon={inviteLoading ? <CircularProgress size={20} /> : <PersonAdd />}
          >
            {formatMessage(messages.sendInvitation)}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

TeamPage.loader = store => () => {
  const state = store.getState();
  const profile = getProfile(state);
  const token = getToken(state);

  if (!profile || !token) {
    throw new Response('Unauthorized', { status: 401 });
  }

  return null;
};
